// ============================================================================
// Verify Receipt Modal
// ============================================================================
// Branch verifies (or disputes) a receipt recorded by a field agent on a
// commitment. Radio: Verified (default) / Disputed. Optional remarks.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useVerifyReceipt } from 'hooks/api/usePreLitigation';
import type { ReceiptRow } from 'api/types/preLitigation.types';

interface Props {
  isOpen: boolean;
  row: ReceiptRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

type Verdict = 'VERIFIED' | 'DISPUTED';

const formatDate = (v?: string) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return v;
  }
};

const VerifyReceiptModal: React.FC<Props> = ({ isOpen, row, onClose, onSuccess }) => {
  const [verdict, setVerdict] = useState<Verdict>('VERIFIED');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const { run, loading } = useVerifyReceipt();

  useEffect(() => {
    if (isOpen) { setVerdict('VERIFIED'); setRemarks(''); setError(null); }
  }, [isOpen]);

  const handleSaveClick = useCallback(() => {
    if (verdict === 'DISPUTED' && remarks.trim().length < 10) {
      setError('Please provide dispute details (min. 10 characters).');
      return;
    }
    setError(null);
    setSaveConfirmOpen(true);
  }, [verdict, remarks]);

  const handleConfirmSave = useCallback(async () => {
    if (!row) return;
    setSaveConfirmOpen(false);
    try {
      await run(row.id, { verdict, remarks: remarks.trim() || undefined });
      notificationService.toast.success(`Receipt for ${row.caseCode} marked as ${verdict === 'VERIFIED' ? 'verified' : 'disputed'}.`);
      onSuccess();
      onClose();
    } catch {
      notificationService.toast.error('Failed to save verification. Please try again.');
    }
  }, [row, verdict, remarks, run, onSuccess, onClose]);

  if (!isOpen || !row) return null;

  const accentColor = verdict === 'VERIFIED' ? '#16a34a' : '#dc2626';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md">
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: accentColor, borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Verify Receipt</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 1rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div><strong>Case:</strong> {row.caseCode}</div>
              <div><strong>Customer:</strong> {row.customerName}</div>
              <div><strong>Account:</strong> {row.accountNumber}</div>
              <div><strong>Branch:</strong> {row.branchCode ?? '—'}</div>
              <div><strong>Committed ₹:</strong> ₹{row.committedAmount.toLocaleString('en-IN')}</div>
              <div><strong>Committed date:</strong> {formatDate(row.committedDate)}</div>
              <div><strong>Received ₹:</strong> <span style={{ color: '#16a34a', fontWeight: 600 }}>₹{(row.honouredAmount ?? 0).toLocaleString('en-IN')}</span></div>
              <div><strong>Received on:</strong> {formatDate(row.honouredAt)}</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Received status:</strong>{' '}
                <span style={{
                  fontSize: '0.72rem', fontWeight: 600,
                  padding: '2px 8px', borderRadius: '999px',
                  color: row.honouredStatus === 'HONOURED' ? '#16a34a' : '#d97706',
                  border: `1px solid ${row.honouredStatus === 'HONOURED' ? '#16a34a' : '#d97706'}`,
                  marginLeft: '0.25rem',
                }}>{row.honouredStatus === 'HONOURED' ? 'Fully Honoured' : 'Partial'}</span>
                <span style={{ marginLeft: '0.75rem', color: 'var(--text-secondary)' }}>
                  captured by {row.capturedByName ?? '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Verdict <span className="text-danger">*</span></label>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="radio"
                  name="verdict"
                  value="VERIFIED"
                  checked={verdict === 'VERIFIED'}
                  onChange={() => { setVerdict('VERIFIED'); setError(null); }}
                />
                <span style={{ color: '#16a34a', fontWeight: 600 }}>Verified</span>
                <span style={{ color: 'var(--text-secondary)' }}>— receipt confirmed against branch records</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="radio"
                  name="verdict"
                  value="DISPUTED"
                  checked={verdict === 'DISPUTED'}
                  onChange={() => setVerdict('DISPUTED')}
                />
                <span style={{ color: '#dc2626', fontWeight: 600 }}>Disputed</span>
                <span style={{ color: 'var(--text-secondary)' }}>— amount or date does not match branch records</span>
              </label>
            </div>
          </div>

          <div className="form-group" data-field="remarks">
            <label className="form-label">
              Remarks {verdict === 'DISPUTED' && <span className="text-danger">*</span>}
              {verdict !== 'DISPUTED' && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>}
            </label>
            <textarea
              className={`form-textarea${error ? ' form-field-error' : ''}`}
              rows={4}
              value={remarks}
              maxLength={2000}
              placeholder={verdict === 'DISPUTED'
                ? 'Explain the discrepancy (amount, date, reference #, etc.)...'
                : 'Optional confirmation notes...'}
              onChange={(e) => { setRemarks(e.target.value); if (error) setError(null); }}
            />
            {error && <div className="form-error-message">{error}</div>}
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textAlign: 'right', marginTop: '0.25rem' }}>
              {remarks.length}/2000
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className={verdict === 'VERIFIED' ? 'btn btn-primary' : 'btn btn-danger'}
            onClick={handleSaveClick} disabled={loading}
          >
            Save Verdict
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={verdict === 'VERIFIED' ? 'Verify Receipt' : 'Dispute Receipt'}
        message={verdict === 'VERIFIED'
          ? `Mark receipt for ${row.caseCode} (₹${(row.honouredAmount ?? 0).toLocaleString('en-IN')}) as VERIFIED?`
          : `Mark receipt for ${row.caseCode} (₹${(row.honouredAmount ?? 0).toLocaleString('en-IN')}) as DISPUTED?`}
        confirmLabel={verdict === 'VERIFIED' ? 'Verify' : 'Dispute'}
        cancelLabel="Cancel"
        variant={verdict === 'VERIFIED' ? 'success' : 'danger'}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </>
  );
};

export default VerifyReceiptModal;
